import React from "react";
import { FixedSizeList } from "react-window";

/**
 * Virtualized wide table.
 * Only rows inside the viewport (+overscan) mount, so 1000+ rows stay smooth.
 *
 * The list's own scrolling box is sized to the viewport (not the full column
 * width), so its native scrollbars (vertical AND horizontal) hug the visible
 * box instead of sitting at the far edge of the wide content — you don't have
 * to scroll sideways first just to reach the vertical scrollbar. The header
 * lives outside the list (so it isn't virtualized away) and is kept in sync
 * with the list's horizontal scroll via a transform.
 *
 * Props:
 *  columns: array of column defs, each:
 *    { key, label, width, header?, renderCell? }
 *      key        unique id + fallback passed to the table-level renderCell
 *      label      header text (used when `header` is absent)
 *      header     custom header node (e.g. filter popover) — overrides label
 *      width      column width in px (default 160)
 *      renderCell (row, rowIndex) => node — per-column override of renderCell
 *  rows:          array of row objects
 *  renderCell:    (row, rowIndex, colKey) => node   fallback cell renderer
 *  cellClassName: (row, colKey) => string           optional per-cell class
 *  rowClassName:  (row, rowIndex) => string          optional per-row class
 *  height:        body viewport height in px (default 520)
 *  rowHeight:     fixed row height in px (default 120)
 *
 * NOTE: the row renderer (VirtualRow) is a STABLE module-level component and
 * receives everything through react-window's `itemData`. Defining it inline in
 * render() would give it a new identity every re-render, causing react-window
 * to remount every row — which makes controlled inputs lose focus after one
 * keystroke. Keep it out here.
 */
const colWidth = (c) => c.width || 160;

function VirtualRow({ index, style, data }) {
  const { columns, rows, renderCell, cellClassName, rowClassName } = data;
  const row = rows[index];
  const extraRow = rowClassName ? rowClassName(row, index) : "";
  const alt = index % 2 === 1 ? "vt-alt" : "";
  return (
    <div
      style={{ ...style, display: "flex", width: "max-content" }}
      className={`vt-row ${alt} ${extraRow}`}
    >
      {columns.map((c) => {
        const extra = cellClassName ? cellClassName(row, c.key) : "";
        const cell = c.renderCell
          ? c.renderCell(row, index)
          : renderCell(row, index, c.key);
        return (
          <div
            key={c.key}
            className={`vt-td form-group ${extra}`}
            style={{ width: colWidth(c), flex: "0 0 auto" }}
          >
            {cell}
          </div>
        );
      })}
    </div>
  );
}

class VirtualTable extends React.Component {
  headerRef = React.createRef();
  outerNode = null;

  setOuterRef = (node) => {
    if (this.outerNode) {
      this.outerNode.removeEventListener("scroll", this.handleListScroll);
    }
    this.outerNode = node;
    if (node) {
      node.addEventListener("scroll", this.handleListScroll, {
        passive: true,
      });
    }
  };

  handleListScroll = () => {
    if (this.headerRef.current && this.outerNode) {
      this.headerRef.current.style.transform = `translateX(-${this.outerNode.scrollLeft}px)`;
    }
  };

  render() {
    const {
      columns = [],
      rows = [],
      renderCell,
      cellClassName,
      rowClassName,
      height = 520,
      rowHeight = 120,
    } = this.props;

    const totalWidth = columns.reduce((sum, c) => sum + colWidth(c), 0);

    const itemData = {
      columns,
      rows,
      renderCell,
      cellClassName,
      rowClassName,
    };

    return (
      <div className="vt-scroll">
        <div className="vt-header-clip">
          <div
            ref={this.headerRef}
            className="vt-header"
            style={{ display: "flex", width: totalWidth }}
          >
            {columns.map((c) => (
              <div
                key={c.key}
                className="vt-th"
                style={{ width: colWidth(c), flex: "0 0 auto" }}
              >
                {c.header !== undefined ? c.header : c.label}
              </div>
            ))}
          </div>
        </div>
        {rows.length > 0 && (
          <FixedSizeList
            height={Math.min(height, rows.length * rowHeight)}
            itemCount={rows.length}
            itemSize={rowHeight}
            width="100%"
            outerRef={this.setOuterRef}
            overscanCount={6}
            itemData={itemData}
          >
            {VirtualRow}
          </FixedSizeList>
        )}
      </div>
    );
  }
}

export default VirtualTable;
